import type { ReactNode } from 'react';
import { useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCloudArrowDown,
    faFilterCircleXmark,
    faVolumeHigh,
} from '@fortawesome/free-solid-svg-icons';

import { SoundClipAlarmSource } from '@/graphql/types';
import { BasePage } from '../BasePage';
import type { TableProps } from '../../table/Table';
import Table from '../../table/Table';
import { format } from '../../../utils/formatting';
import { getBrowserTimezone } from '../../../utils/get-browser-timezone';
import { useOrderAndPaginationControl } from '../../table/hooks';
import type { SoundClipNode } from './utils';
import useSoundClipQueryGenerator, { useSoundClipsFiltrationControl } from './utils';
import Pagination from '../../table/Pagination';
import LoadingSpinner from '../../loadingSpinner/LoadingSpinner';
import DateRangePicker from '../../dateRangePicker/DateRangePicker';
import MeasuringPointSelect from '../../measuringPointSelect/MeasuringPointSelect';
import { RoundedButton } from '../../form/Button';
import type { RoundedButtonProps } from '../../form/Button';
import { DATA_TYPE } from '../../../graph/enums';
import { EmptyState } from '../../emptyState/EmptyState';
import { SwarmType } from '../../../enums';
import { useMeasuringPointsQuery } from '../../graph/components/utils';
import warning from '../../../utils/logger';

type TableColumn = TableProps<SoundClipNode>['columns'][number];
const EMPTY_STATE_VARIANT = 'brand';
const SOUND_CLIPS_EMPTY_STATE_ICON = faVolumeHigh;
const CLEAR_FILTERS_ICON = faFilterCircleXmark;
export const SOUND_CLIPS_TABLE_SORT = {
    tableId: 'sound-clips',
    sortableFields: ['startTime', 'endTime'],
} as const;

function getTriggerSourceLabel(triggerSource?: SoundClipAlarmSource | null): string {
    switch (triggerSource) {
        case SoundClipAlarmSource.Leq:
            return 'Leq';
        case SoundClipAlarmSource.Lmax:
            return 'Lmax';
        case SoundClipAlarmSource.Lpeak:
            return 'Lpeak';
        default:
            return gettext('NOT_APPLICABLE');
    }
}

function getGraphDataTypeByTriggerSource(
    triggerSource?: SoundClipAlarmSource | null
): string | null {
    switch (triggerSource) {
        case SoundClipAlarmSource.Leq:
            return DATA_TYPE.LXEQ;
        case SoundClipAlarmSource.Lmax:
            return DATA_TYPE.LXTMAX;
        case SoundClipAlarmSource.Lpeak:
            return DATA_TYPE.LXPEAK;
        default:
            return null;
    }
}

function SoundClipsPage(): JSX.Element {
    const filtrationControl = useSoundClipsFiltrationControl();
    const orderAndPaginationControl = useOrderAndPaginationControl(SOUND_CLIPS_TABLE_SORT);
    const {
        measuringPoints,
        loading: measuringPointsLoading,
        error: measuringPointsError,
    } = useMeasuringPointsQuery({
        swarmType: SwarmType.Sound,
    });

    const {
        filters: { mpPk, startDate, endDate },
        setDateRangeFilter,
        setMeasuringPointFilter,
        clearDateFilters,
    } = filtrationControl;

    useEffect(() => {
        if (!measuringPointsError) {
            return;
        }

        warning(`[Sound clips] Failed to load measuring points: ${measuringPointsError.message}`, {
            mpPk,
        });
    }, [measuringPointsError, mpPk]);

    const selectedMeasuringPoint = measuringPoints.find(
        (measuringPoint) => measuringPoint.id === mpPk
    );
    const isSelectedMeasuringPointLoading =
        Boolean(mpPk) && measuringPointsLoading && !selectedMeasuringPoint;
    const timezone = selectedMeasuringPoint?.timezone?.replace('_', '/') ?? getBrowserTimezone();

    const { soundClips, loading, updating } = useSoundClipQueryGenerator(
        orderAndPaginationControl,
        filtrationControl,
        timezone,
        isSelectedMeasuringPointLoading
    );
    const isContentLoading = isSelectedMeasuringPointLoading || loading || updating;

    const columns = useMemo<TableColumn[]>(
        () => [
            {
                key: 'startTime',
                title: gettext('START_TIME'),
                render(_column: TableColumn, row: SoundClipNode): ReactNode {
                    return format(new Date(row.startTime as number), { timezone });
                },
            },
            {
                key: 'endTime',
                title: <span className="inline-block pl-14">{gettext('END_TIME')}</span>,
                render(_column: TableColumn, row: SoundClipNode): ReactNode {
                    return (
                        <div className="pl-14">
                            {format(new Date(row.endTime as number), { timezone })}
                        </div>
                    );
                },
            },
            {
                key: 'triggerValue',
                title: (
                    <span className="inline-block pl-8">{gettext('EXCEEDANCE_TRIGGER_IN_DB')}</span>
                ),
                render(_column: TableColumn, row: SoundClipNode): ReactNode {
                    const displayValue =
                        row.triggerValue == null
                            ? gettext('NOT_APPLICABLE')
                            : `${row.triggerValue.toFixed(1)} dB`;

                    return <span className="inline-block pl-8 tabular-nums">{displayValue}</span>;
                },
            },
            {
                key: 'triggerSource',
                title: gettext('ATTRIBUTE_TYPE_FIELD'),
                render(_column: TableColumn, row: SoundClipNode): ReactNode {
                    return getTriggerSourceLabel(row.triggerSource);
                },
            },
            {
                key: 'waveFile',
                title: (
                    <span className="space-x-2">
                        {(updating || isSelectedMeasuringPointLoading) && (
                            <LoadingSpinner className="h-5 align-middle text-gray-400" />
                        )}
                        {orderAndPaginationControl.showPagination && (
                            <Pagination orderAndPaginationControl={orderAndPaginationControl} />
                        )}
                    </span>
                ),
                render(_column: TableColumn, row: SoundClipNode): ReactNode {
                    const graphQueryParams = new URLSearchParams({
                        start: String(row.startTime),
                        end: String(row.endTime),
                    });
                    const graphDataType = getGraphDataTypeByTriggerSource(row.triggerSource);
                    if (graphDataType) {
                        graphQueryParams.set('dataType', graphDataType);
                    }
                    const graphUrl = `/sbr/sbrView/?${graphQueryParams.toString()}#${mpPk}`;

                    const graphButtonLinkProps = {
                        href: graphUrl,
                        target: '_blank',
                        rel: 'noopener noreferrer',
                    } as unknown as RoundedButtonProps;

                    return (
                        <div className="flex items-center justify-end gap-4">
                            <audio controls preload="none" className="h-8 w-72">
                                <source src={row.streamUrl ?? ''} type="audio/wav" />
                                {gettext('AUDIO_PLAYER_NOT_SUPPORTED')}
                            </audio>
                            <RoundedButton {...graphButtonLinkProps}>
                                {gettext('VIEW_IN_GRAPH')}
                            </RoundedButton>
                            <a href={row.downloadUrl!} download className="hover:text-primary">
                                <FontAwesomeIcon
                                    icon={faCloudArrowDown}
                                    className="h-8 w-8 fill-current"
                                />
                            </a>
                        </div>
                    );
                },
            },
        ],
        [timezone, updating, isSelectedMeasuringPointLoading, orderAndPaginationControl, mpPk]
    );

    return (
        <BasePage title={gettext('SOUND_CLIPS')}>
            <div className="mb-14 pb-4 pt-2">
                <div className="mb-4 flex w-full flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div className="w-full md:w-1/2">
                        <MeasuringPointSelect
                            className="w-full"
                            selectId="mp-selector-sound-clips-page"
                            onSelectChange={setMeasuringPointFilter}
                            initialValue={mpPk}
                            measuringPoints={measuringPoints}
                            allowNoSelection={true}
                            noSelectionText={gettext('SELECT_A_MEASURING_POINT')}
                        />
                    </div>
                    <div className="flex w-full flex-col gap-2 md:w-auto md:items-end">
                        <DateRangePicker
                            startDate={startDate}
                            endDate={endDate}
                            timezone={timezone}
                            isTimezoneReady={!isSelectedMeasuringPointLoading}
                            onDateRangeChange={setDateRangeFilter}
                        />
                        <span>{`${gettext('TIMEZONE')}: ${timezone}`}</span>
                    </div>
                </div>
                {isContentLoading || soundClips.length > 0 ? (
                    <Table
                        data={soundClips}
                        columns={columns}
                        loading={isContentLoading}
                        orderAndPaginationControl={orderAndPaginationControl}
                    />
                ) : (
                    <EmptyState
                        variant={EMPTY_STATE_VARIANT}
                        iconFontAwesome={SOUND_CLIPS_EMPTY_STATE_ICON}
                        header={gettext('SOUND_CLIPS_EMPTY_HEADING')}
                        description={gettext('SOUND_CLIPS_EMPTY_BODY')}
                        actionButtonText={gettext('CLEAR_FILTERS')}
                        actionButtonIconFontAwesome={CLEAR_FILTERS_ICON}
                        actionButtonMethod={clearDateFilters}
                    />
                )}
            </div>
        </BasePage>
    );
}

export default SoundClipsPage;
